import { useCallback, useEffect, useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { api, type CrmAnalyticsDay, type CrmAnalyticsResponse, type CrmAnalyticsSource } from "@/lib/api";

const sourceOptions: Array<{ value: CrmAnalyticsSource; label: string; description: string }> = [
  { value: "web", label: "Web", description: "Метрики сайта" },
  { value: "telegram", label: "TG mini app", description: "Метрики Telegram mini app" },
];

function formatIso(date: Date) {
  return date.toISOString().slice(0, 10);
}

function buildDefaultRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(to.getDate() - 29);
  return { from: formatIso(from), to: formatIso(to) };
}

function formatLabel(date: string) {
  const [year, month, day] = date.split("-");
  return `${day}.${month}`;
}

function toChartData(days: CrmAnalyticsDay[], key: "visits" | "carts" | "orders") {
  return days.map((day) => ({
    date: formatLabel(day.date),
    value: day[key],
  }));
}

export default function AnalyticsPage() {
  const defaults = useMemo(() => buildDefaultRange(), []);
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CrmAnalyticsResponse | null>(null);
  const [source, setSource] = useState<CrmAnalyticsSource>("web");

  const loadAnalytics = useCallback((from: string, to: string, nextSource: CrmAnalyticsSource) => {
    setLoading(true);
    setError(null);
    api
      .analytics({ date_from: from, date_to: to, source: nextSource })
      .then((response) => setData(response))
      .catch((err) => setError(err instanceof Error ? err.message : "Не удалось загрузить аналитику"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAnalytics(defaults.from, defaults.to, source);
  }, [defaults.from, defaults.to, loadAnalytics, source]);

  const currentSource = useMemo(() => sourceOptions.find((option) => option.value === source) ?? sourceOptions[0], [source]);

  const days = useMemo(() => data?.days ?? [], [data]);
  const visitsChart = useMemo(() => toChartData(days, "visits"), [days]);
  const cartsChart = useMemo(() => toChartData(days, "carts"), [days]);
  const ordersChart = useMemo(() => toChartData(days, "orders"), [days]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Аналитика продаж</h2>
          <p className="mt-1 text-sm text-gray-500">{currentSource.description}: посещения, корзины и заказы.</p>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              {sourceOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSource(option.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    source === option.value ? "bg-amber-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-50"
                  }`}
                  aria-pressed={source === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
            <span className="text-sm text-gray-400">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
          </div>
          <button
            onClick={() => loadAnalytics(dateFrom, dateTo, source)}
            className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
          >
            <i className="ri-refresh-line" />
            Обновить
          </button>
        </div>
      </div>

      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-[#E8E2D8] bg-white py-16">
          <i className="ri-loader-4-line animate-spin text-xl text-amber-600" />
          <span className="text-sm text-gray-500">Загрузка аналитики...</span>
        </div>
      ) : data ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-[#E8E2D8] bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Посещения</p>
              <p className="mt-3 text-3xl font-bold text-gray-900">{data.totals.visits.toLocaleString("ru-RU")}</p>
            </div>
            <div className="rounded-xl border border-[#E8E2D8] bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Корзины</p>
              <p className="mt-3 text-3xl font-bold text-gray-900">{data.totals.carts.toLocaleString("ru-RU")}</p>
            </div>
            <div className="rounded-xl border border-[#E8E2D8] bg-white p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Заказы</p>
              <p className="mt-3 text-3xl font-bold text-gray-900">{data.totals.orders.toLocaleString("ru-RU")}</p>
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-3">
            {[
              { title: "Посещения", chart: visitsChart, stroke: "#d97706", fillId: "visits" },
              { title: "Корзины", chart: cartsChart, stroke: "#ca8a04", fillId: "carts" },
              { title: "Заказы", chart: ordersChart, stroke: "#059669", fillId: "orders" },
            ].map((card) => (
              <div key={card.title} className="rounded-xl border border-[#E8E2D8] bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-800">{card.title}</h3>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={card.chart} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id={card.fillId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={card.stroke} stopOpacity={0.28} />
                          <stop offset="95%" stopColor={card.stroke} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={44} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={card.stroke}
                        strokeWidth={2}
                        fill={`url(#${card.fillId})`}
                        activeDot={{ r: 4, stroke: card.stroke, strokeWidth: 2, fill: "#fff" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
